/**
 * Tenant settings — read-only view using CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 */
import { useEffect, useState } from 'react';
import { Palette, ShieldCheck } from 'lucide-react';

import { Chip, FormField } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../components/layouts/PageFormLayout';
import { settingsService } from '../services/settingsService';
import { useContextStore } from '../store/contextStore';
import { EmptyState, PageContainer } from '@coordio/ui/layout';
import { ShellHeader } from '@coordio/ui';
import type { TenantSettingsDto } from '../types/settings';

/**
 * Tenant settings — read-only view.
 */
export const TenantSettingsPage = () => {
  const { currentTenant } = useContextStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TenantSettingsDto | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!currentTenant) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const settings = await settingsService.getTenantSettings(currentTenant.id);
        if (!cancelled) {
          setData(settings);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load tenant settings');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentTenant?.id]);

  if (!currentTenant) {
    return (
      <div className="tenant-settings" data-testid="tenant-settings-form-page">
        <ShellHeader title="Tenant Settings" description="Choose a workspace to view tenant settings." data-testid="tenant-settings-header" />
        <PageContainer data-testid="tenant-settings-page-container">
          <EmptyState
            title="No workspace selected"
            description="Please select a tenant first to view tenant settings."
            data-testid="tenant-settings-empty-state"
          />
        </PageContainer>
      </div>
    );
  }

  const subtitle = [
    `Tenant: ${currentTenant.name}`,
    ...(data?.tenantId ? [data.tenantId] : []),
  ].join(' \u2022 ');

  const formatBool = (value: boolean) => (value ? 'Yes' : 'No');

  return (
    <PageFormLayout
      title="Tenant Settings"
      subtitle={subtitle}
      data-testid="tenant-settings-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      actions={{
        onSave: () => {},
        onCancel: () => window.history.back(),
        saveLabel: 'Save',
        saveDisabled: true,
      }}
      navGroups={[
        {
          title: 'Settings',
          items: [
            { id: 'branding', label: 'Branding', icon: <Palette size={14} /> },
            { id: 'security', label: 'Security', icon: <ShieldCheck size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'branding',
          title: 'Branding',
          description: 'Visual identity and custom domain settings for the tenant.',
          icon: <Palette size={16} />,
          content: (
            <FormSection title="Appearance" description="Logo, brand color, and custom domain configuration." data-testid="tenant-settings-section-branding">
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Logo URL" description="URL to the tenant logo image." data-testid="tenant-settings-logoUrl">
                  <div className="rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2" data-testid="tenant-settings-logoUrl-value">
                    {data?.logoUrl ? (
                      <span className="text-sm break-all text-[var(--color-text-primary)]">{data.logoUrl}</span>
                    ) : (
                      <span className="text-sm italic text-[var(--color-text-tertiary,#a3a3a3)]">Not set</span>
                    )}
                  </div>
                </FormField>

                <FormField label="Brand Color" description="Primary brand color applied across the tenant UI." data-testid="tenant-settings-brandColor">
                  <div className="flex items-center gap-2 rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2" data-testid="tenant-settings-brandColor-value">
                    {data?.brandColor && (
                      <div
                        className="h-5 w-5 rounded border border-[var(--color-border-default)]"
                        style={{ backgroundColor: data.brandColor }}
                      />
                    )}
                    {data?.brandColor ? (
                      <span className="text-sm font-mono text-[var(--color-text-primary)]">{data.brandColor}</span>
                    ) : (
                      <span className="text-sm italic text-[var(--color-text-tertiary,#a3a3a3)]">Not set</span>
                    )}
                  </div>
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Custom Domain" description="Custom domain mapped to this tenant." data-testid="tenant-settings-customDomain">
                  <div className="rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2" data-testid="tenant-settings-customDomain-value">
                    {data?.customDomain ? (
                      <span className="text-sm text-[var(--color-text-primary)]">{data.customDomain}</span>
                    ) : (
                      <span className="text-sm italic text-[var(--color-text-tertiary,#a3a3a3)]">Not set</span>
                    )}
                  </div>
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'security',
          title: 'Security',
          description: 'Authentication, session, and password policy settings.',
          icon: <ShieldCheck size={16} />,
          content: (
            <FormSection title="Security policies" description="Multi-factor authentication, session timeout, and password rules." data-testid="tenant-settings-section-security">
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Require MFA" description="Whether multi-factor authentication is enforced." data-testid="tenant-settings-requireMfa">
                  <div data-testid="tenant-settings-requireMfa-value">
                    <Chip
                      label={data?.requireMfa ? 'Required' : 'Optional'}
                      color={data?.requireMfa ? 'green' : 'violet'}
                      variant="outlined"
                    />
                  </div>
                </FormField>

                <FormField label="Session Timeout" description="Number of minutes before an idle session expires." data-testid="tenant-settings-sessionTimeout">
                  <div className="rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2" data-testid="tenant-settings-sessionTimeout-value">
                    <span className="text-sm text-[var(--color-text-primary)]">{data?.sessionTimeoutMinutes ?? '—'} minutes</span>
                  </div>
                </FormField>

                <FormField label="Password Minimum Length" description="Minimum number of characters required for passwords." data-testid="tenant-settings-passwordMinLength">
                  <div className="rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2" data-testid="tenant-settings-passwordMinLength-value">
                    <span className="text-sm text-[var(--color-text-primary)]">{data?.passwordMinLength ?? '—'} characters</span>
                  </div>
                </FormField>

                <FormField label="Password Requirements" description="Character type requirements for tenant passwords." data-testid="tenant-settings-passwordRequirements">
                  <div className="flex flex-col gap-1.5" data-testid="tenant-settings-passwordRequirements-value">
                    {[
                      { label: 'Uppercase', value: data?.passwordRequireUppercase },
                      { label: 'Lowercase', value: data?.passwordRequireLowercase },
                      { label: 'Number', value: data?.passwordRequireNumber },
                      { label: 'Special character', value: data?.passwordRequireSpecialChar },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center gap-2 text-sm">
                        <span className={`inline-block h-2 w-2 rounded-full ${value ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-[var(--color-text-secondary)]">{label}:</span>
                        <span className={`font-medium ${value ? 'text-green-700' : 'text-[var(--color-text-tertiary,#a3a3a3)]'}`}>
                          {formatBool(value ?? false)}
                        </span>
                      </div>
                    ))}
                  </div>
                </FormField>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
